import { useState, useEffect } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Gauge, Fuel, Settings, MapPin, CheckCircle, ChevronLeft, ChevronRight, MessageCircle, Phone } from 'lucide-react';
import { useCars } from '../context/CarContext';
import { BUSINESS_CONFIG, createWhatsAppUrl, formatCarInfo } from '../config/business';
import { 
  updatePageTitle, 
  updateMetaDescription, 
  updateCanonicalUrl,
  generateCarStructuredData,
  addStructuredData,
  generateCarSEOTitle,
  generateCarSEODescription
} from '../utils/seoUtils';

const CarDetails = () => {
  const { id } = useParams<{ id: string }>();
  const { getCarById } = useCars();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  
  if (!id) {
    return <Navigate to="/" replace />;
  }
  
  const car = getCarById(id);
  
  // SEO optimization for car details page
  useEffect(() => {
    if (car) {
      const seoTitle = generateCarSEOTitle(car);
      const seoDescription = generateCarSEODescription(car);
      const currentUrl = window.location.href;
      const structuredData = generateCarStructuredData(car);
      
      updatePageTitle(seoTitle);
      updateMetaDescription(seoDescription);
      updateCanonicalUrl(currentUrl);
      addStructuredData(structuredData);
    }
  }, [car]);
  
  if (!car) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-400 mb-4">
          <MapPin className="h-16 w-16 mx-auto" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Vehículo no encontrado</h2>
        <p className="text-gray-600 mb-6">El vehículo que buscas no existe o ha sido removido.</p>
        <Link to="/" className="btn-primary">
          Volver al Inventario
        </Link>
      </div>
    );
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatMileage = (mileage: number) => {
    return new Intl.NumberFormat('es-US').format(mileage);
  };

  // Navigation functions for image gallery
  const nextImage = () => {
    if (car.images && car.images.length > 0) {
      setSelectedImageIndex((prev) => 
        prev === car.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (car.images && car.images.length > 0) {
      setSelectedImageIndex((prev) => 
        prev === 0 ? car.images.length - 1 : prev - 1
      );
    }
  };

  // WhatsApp contact function
  const contactSeller = () => {
    const carInfo = formatCarInfo(car);
    const message = `${BUSINESS_CONFIG.messages.interested} ${carInfo}. Could you please provide more information?`;
    const whatsappUrl = createWhatsAppUrl(message);
    window.open(whatsappUrl, '_blank');
  };

  const scheduleTest = () => {
    const carInfo = formatCarInfo(car);
    const message = `${BUSINESS_CONFIG.messages.testDrive} ${carInfo}. When would be a good time?`;
    const whatsappUrl = createWhatsAppUrl(message);
    window.open(whatsappUrl, '_blank');
  };

  const selectImage = (index: number) => {
    setSelectedImageIndex(index);
  };

  const currentImage = car.images && car.images.length > 0 ? car.images[selectedImageIndex] : null;

  // Keyboard navigation support
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (car.images && car.images.length > 1) {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          prevImage();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          nextImage();
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [car.images]);

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Link 
        to="/" 
        className="inline-flex items-center space-x-2 text-primary-600 hover:text-primary-700 transition-colors"
      >
        <ArrowLeft className="h-5 w-5" />
        <span>Volver al inventario</span>
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Interactive Image Gallery */}
        <div className="space-y-4">
          {/* Main Image Display */}
          <div className="relative bg-gray-200 rounded-xl overflow-hidden group">
            {currentImage ? (
              <>
                <img
                  src={currentImage}
                  alt={`${car.make} ${car.model} - Imagen ${selectedImageIndex + 1}`}
                  className="w-full h-[600px] object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = `https://via.placeholder.com/800x600/e5e7eb/6b7280?text=${car.make}+${car.model}`;
                  }}
                />
                
                {/* Navigation Arrows (only show if multiple images) */}
                {car.images && car.images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                      aria-label="Imagen anterior"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                      aria-label="Siguiente imagen"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                    
                    {/* Image Counter */}
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                      {selectedImageIndex + 1} / {car.images.length}
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="w-full h-[600px] flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <MapPin className="h-20 w-20 mx-auto mb-4" />
                  <p className="text-lg font-medium">{car.make} {car.model}</p>
                </div>
              </div>
            )}
          </div>
          
          {/* Thumbnail Gallery */}
          {car.images && car.images.length > 1 && (
            <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2">
              {car.images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => selectImage(index)}
                  className={`relative aspect-square bg-gray-200 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                    selectedImageIndex === index
                      ? 'border-primary-500 ring-2 ring-primary-200'
                      : 'border-transparent hover:border-gray-300'
                  }`}
                >
                  <img
                    src={image}
                    alt={`${car.make} ${car.model} - Miniatura ${index + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = `https://via.placeholder.com/150x150/e5e7eb/6b7280?text=${index + 1}`;
                    }}
                  />
                  {selectedImageIndex === index && (
                    <div className="absolute inset-0 bg-primary-500/20 flex items-center justify-center">
                      <div className="w-2 h-2 bg-primary-600 rounded-full"></div>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
          
          {/* Image Navigation with Keyboard Support */}
          {car.images && car.images.length > 1 && (
            <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
              <span>Usa las flechas del teclado o haz clic en las miniaturas para navegar</span>
            </div>
          )}
        </div>

        {/* Car Information */}
        <div className="space-y-6">
          {/* Header */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h1 className="text-3xl font-bold text-gray-900">
                {car.year} {car.make} {car.model}
              </h1>
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                car.condition === 'New' 
                  ? 'bg-green-100 text-green-800' 
                  : car.condition === 'Certified Pre-Owned'
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                {car.condition}
              </span>
            </div>
            <p className="text-4xl font-bold text-primary-600 mb-2">{formatPrice(car.price)}</p>
            <p className="text-lg text-gray-600 capitalize">{car.color}</p>
          </div>

          {/* Key Specs */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
              <Calendar className="h-6 w-6 text-primary-600" />
              <div>
                <p className="text-sm text-gray-600">Año</p>
                <p className="font-semibold">{car.year}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
              <Gauge className="h-6 w-6 text-primary-600" />
              <div>
                <p className="text-sm text-gray-600">Millaje</p>
                <p className="font-semibold">{formatMileage(car.mileage)} mi</p>
              </div>
            </div>
            <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
              <Fuel className="h-6 w-6 text-primary-600" />
              <div>
                <p className="text-sm text-gray-600">Combustible</p>
                <p className="font-semibold">{car.fuelType}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
              <Settings className="h-6 w-6 text-primary-600" />
              <div>
                <p className="text-sm text-gray-600">Transmisión</p>
                <p className="font-semibold">{car.transmission}</p>
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Descripción</h3>
            <p className="text-gray-700 leading-relaxed">{car.description}</p>
          </div>

          {/* Features */}
          {car.features && car.features.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Características</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {car.features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Contact Action */}
          <div className="bg-primary-50 p-6 rounded-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">¿Interesado en este vehículo?</h3>
            <p className="text-gray-700 mb-4">
              Contáctanos para más información, programar una prueba de manejo o negociar el precio.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button 
                onClick={contactSeller}
                className="btn-primary flex-1 flex items-center justify-center space-x-2"
              >
                <MessageCircle className="h-5 w-5" />
                <span>Contactar Vendedor</span>
              </button>
              <button 
                onClick={scheduleTest}
                className="btn-secondary flex-1 flex items-center justify-center space-x-2"
              >
                <Phone className="h-5 w-5" />
                <span>Programar Prueba</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarDetails;